import {useRef} from 'react';

import {isNotNilOrEmpty, isString} from 'ramda-adjunct';

import {useOnMount} from 'shared/web';

import {useHttpCalls} from '../api/useHttpCalls';
import {quickFiltersConst} from '../constants/quickFilters';
import {selectFilterValues, selectSuggestionLabelsCache} from '../store/createEagGridStore';
import {useEagGridSelector} from '../store/useEagGridSelector';
import {useEagGridStore} from '../store/useEagGridStore';
import type {EagColumnOrGroup} from '../types/EagColumnGroup';
import {flattenColumnConfig} from '../utils/flattenColumnConfig';
import {isFilterValueActive, unwrapFilterValue} from '../utils/isFilterValueActive';

type SuggestColumn = {columnKey: string; suggestId: string};

/** Unwraps a MULTI_CHOICE_ASYNC filter value into the concrete option keys that need a
 *  label — a single string or a string[]. Quick-filter sentinels (is-empty / is-filled)
 *  are not real option keys and must never be sent to the suggestion API. */
const toResolvableValues = (raw: unknown): string[] => {
  const value = unwrapFilterValue(raw);
  const list = Array.isArray(value) ? value : [value];
  return list.filter(isString).filter((entry) => !quickFiltersConst.includes(entry));
};

/**
 * Resolves human labels for active async (MULTI_CHOICE_ASYNC) filter values and caches
 * them in the store, so the active-filter bar can show a name instead of the raw key
 * (e.g. a branch UUID). The label endpoint is otherwise only hit when the filter editor
 * opens, so a filter hydrated from the URL or a preset would render its raw key forever.
 *
 * Trigger model (no `useEffect`, per eag-grid rules): resolve once on mount (the cold
 * load — filters may already be hydrated), then re-resolve on every `filterValues` ref
 * change via a store subscription (preset switch, URL hydration, or user edit). Values
 * already in the cache or with an in-flight request are skipped, so repeated triggers
 * never re-fetch the same key. The cache is shared with the SuggestionFilter editor,
 * which writes through the labels it fetches, so an already-opened filter needs no fetch.
 *
 * Returns the whole `suggestionLabelsCache` (stable reference until it changes) for the
 * caller to hand to `buildActiveFilterChips`.
 */
export function useResolveSuggestionFilterLabels(columnConfig: EagColumnOrGroup[]) {
  const store = useEagGridStore();
  const http = useHttpCalls();
  const inFlightRef = useRef<Set<string>>(new Set());
  const unsubscribeRef = useRef<(() => void) | null>(null);

  // columnConfig is stable after mount (see EagGridTable), so the async-column set is too.
  // The subscription reads live filterValues / cache from the store at fire time.
  const suggestColumns: SuggestColumn[] = flattenColumnConfig(columnConfig)
    .filter((column) => isNotNilOrEmpty(column.filter?.suggestId))
    .map((column) => ({columnKey: column.key, suggestId: column.filter!.suggestId!}));

  const resolveMissingLabels = () => {
    if (suggestColumns.length === 0) {
      return;
    }
    const state = store.getState();
    const filterValues = selectFilterValues(state);
    const cache = state.suggestionLabelsCache;

    const missingBySuggestId = new Map<string, Set<string>>();
    suggestColumns.forEach(({columnKey, suggestId}) => {
      const filterState = filterValues[columnKey];
      if (!isFilterValueActive(filterState)) {
        return;
      }
      const cachedInner = cache.get(suggestId);
      toResolvableValues(filterState).forEach((value) => {
        if (cachedInner?.has(value) || inFlightRef.current.has(`${suggestId}:${value}`)) {
          return;
        }
        const pending = missingBySuggestId.get(suggestId) ?? new Set<string>();
        pending.add(value);
        missingBySuggestId.set(suggestId, pending);
      });
    });

    missingBySuggestId.forEach((valueSet, suggestId) => {
      const keys = [...valueSet];
      keys.forEach((value) => inFlightRef.current.add(`${suggestId}:${value}`));
      http
        .getSuggestionLabels({suggestId, keys})
        .then((result) => {
          store.setSuggestionLabels(suggestId, result ?? []);
        })
        // getSuggestionLabels is best-effort and resolves `undefined` on failure
        // (it never blanks the grid — see ApiContext.handleBestEffortError). Swallow
        // defensively anyway so a contract change can never surface an unhandled
        // rejection; the value simply stays as its raw key until a later resolve.
        .catch(() => undefined)
        .finally(() => {
          keys.forEach((value) => inFlightRef.current.delete(`${suggestId}:${value}`));
        });
    });
  };

  useOnMount(
    () => {
      resolveMissingLabels();
      unsubscribeRef.current = store.subscribe((state, prev) => {
        if (state.filterValues !== prev.filterValues) {
          resolveMissingLabels();
        }
      });
    },
    () => {
      unsubscribeRef.current?.();
      unsubscribeRef.current = null;
    }
  );

  return useEagGridSelector(selectSuggestionLabelsCache);
}
